import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function oauthApi(): OAuthApi | null {
  const api = (supabase.auth as unknown as { oauth?: OAuthApi }).oauth;
  return api ?? null;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [needsLogin, setNeedsLogin] = useState(false);
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Falta el parametro authorization_id.");
        return;
      }
      const api = oauthApi();
      if (!api) {
        setError("Este proyecto de Supabase todavia no tiene activado el servidor OAuth 2.1.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        if (active) setNeedsLogin(true);
        return;
      }
      const { data, error: detailsError } = await api.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (detailsError) {
        setError(detailsError.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setNeedsLogin(false);
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId, reload]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    setReload((n) => n + 1);
  }

  async function decide(approve: boolean) {
    const api = oauthApi();
    if (!api) return;
    setBusy(true);
    setError(null);
    const { data, error: decideError } = approve
      ? await api.approveAuthorization(authorizationId)
      : await api.denyAuthorization(authorizationId);
    if (decideError) {
      setBusy(false);
      setError(decideError.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("El servidor de autorizacion no devolvio ninguna redireccion.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "esta aplicacion";

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-6 py-24">
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-border bg-card p-8 shadow-sm">
        <h1 className="font-serif text-2xl text-foreground">Conectar con MassHappy</h1>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {needsLogin && (
          <form onSubmit={handleLogin} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Inicia sesion para autorizar el acceso a tu cuenta.
            </p>
            <div className="space-y-2">
              <Label htmlFor="consent-email">Email</Label>
              <Input
                id="consent-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="consent-password">Contrasena</Label>
              <Input
                id="consent-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Entrando..." : "Iniciar sesion"}
            </Button>
          </form>
        )}

        {!needsLogin && !details && !error && (
          <p className="text-sm text-muted-foreground">Cargando la solicitud...</p>
        )}

        {details && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {clientName} quiere conectarse a tu cuenta de MassHappy y usar las herramientas en tu nombre.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" disabled={busy} onClick={() => decide(false)}>
                Denegar
              </Button>
              <Button className="flex-1" disabled={busy} onClick={() => decide(true)}>
                Autorizar
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
